<template>
  <!-- ProProxyUI: settings read from the box; a changed value is written to the box when the server is saved -->
  <div class="float-left w-full px-2 pb-2 modal-item">
    <div class="ppbx">
      <div class="ppbx-head">
        <b>Box settings</b>
        <span class="ppbx-state">{{ state }}</span>
        <button type="button" class="ppbx-btn" :disabled="loading" @click="read">{{ loading ? 'Reading…' : 'Read from box' }}</button>
      </div>
      <small v-if="!loaded" class="ppbx-dim">Enter the Server URL and API token above: the box's settings appear here.</small>
      <div v-else class="ppbx-grid">
        <div class="ppbx-wide"><label>Address customers connect to</label><input v-model="model.publicHost" type="text" class="form-control shadow-none rounded-0 text-black"></div>
        <div><label>Proxy ports from</label><input v-model.number="model.portRangeStart" type="number" min="1" max="65535" class="form-control shadow-none rounded-0 text-black"></div>
        <div><label>Proxy ports to</label><input v-model.number="model.portRangeEnd" type="number" min="1" max="65535" class="form-control shadow-none rounded-0 text-black"></div>
        <div><label>WireGuard port (shared)</label><input v-model.number="model.wgPublicPort" type="number" min="1" max="65535" class="form-control shadow-none rounded-0 text-black"></div>
        <div><label>OpenVPN port (shared)</label><input v-model.number="model.ovpnPublicPort" type="number" min="1" max="65535" class="form-control shadow-none rounded-0 text-black"></div>
        <div><label>OpenVPN protocol</label>
          <select v-model="model.ovpnProto" class="form-control shadow-none rounded-0 text-black"><option value="udp">UDP</option><option value="tcp">TCP</option></select></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits } from 'vue';
import { fetchWrapper } from '@/helpers';

const props = defineProps({ modelValue: { type: Object, default: () => ({}) }, domain: String, username: String, password: String, uuid: String });
const emit = defineEmits(['update:modelValue']);
// the form edits its own copy; every change goes back to the dialog (v-model) and is saved as box_settings
const model = ref({});
watch(model, (v) => emit('update:modelValue', { ...v }), { deep: true });
const API = `${process.env.VUE_APP_VUE_API_URL}`;
const loading = ref(false);
const loaded = ref(false);
const state = ref('Enter the box address and API token');

const read = async () => {
  if (!props.domain || (!props.password && !props.uuid)) { state.value = 'Enter the box address and API token'; return; }
  loading.value = true;
  try {
    const r = await fetchWrapper.post(`${API}/admin/proproxyui/read-settings`, { domain: props.domain, username: props.username || '', password: props.password || '', uuid: props.uuid || undefined });
    model.value = { ...(r.settings || {}) };
    loaded.value = true;
    state.value = r.box ? `v${r.box.version} · ${r.box.modems_online} of ${r.box.modems_total} modems online · ${r.box.ports} ports` : 'Read from the box';
  } catch (e) {
    loaded.value = false;
    model.value = {};
    state.value = typeof e === 'string' ? e : 'The box did not answer';
  }
  loading.value = false;
};
// read once the address and token are in (and again when either changes)
let timer = null;
watch(() => [props.domain, props.password, props.uuid], () => { clearTimeout(timer); timer = setTimeout(read, 700); }, { immediate: true });
</script>

<style scoped>
.ppbx { border: 1px solid #dee2e6; border-radius: 10px; padding: 10px 12px; }
.ppbx-head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.ppbx-state { color: #6b7280; font-size: 12px; flex: 1; }
.ppbx-btn { padding: 3px 10px; border: 1px solid #adb5bd; border-radius: 6px; font-size: 12px; background: transparent; color: inherit; cursor: pointer; }
.ppbx-btn:disabled { opacity: 0.5; cursor: default; }
.ppbx-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 12px; margin-top: 10px; }
.ppbx-wide { grid-column: 1 / -1; }
.ppbx-dim { display: block; margin-top: 6px; color: #6b7280; font-size: 12px; }
.ppbx-grid label { display: block; color: #6b7280; font-size: 12px; margin-bottom: 2px; }
</style>

<style>
body.dark .ppbx { border-color: #45445e; background: #34334a; }
body.dark .ppbx-state, body.dark .ppbx-dim, body.dark .ppbx-grid label { color: #bcbbc7; }
body.dark .ppbx-btn { border-color: #45445e; }
</style>
